import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from './storage.service';

export type DocumentType = 'COTIZACIONES' | 'PEDIDOS';

export interface MobileSettings {
  expiration_days_alert_inventory_client: number;
  min_stock_quantity_alert_inventory_client: number;
  inventory_alerts_enabled: boolean;
  validate_order_stock: boolean;
  document_type: DocumentType;
  show_inventory_in_mobile: boolean;
  quotation_validity_days: number;
  max_order_items: number;
  max_discount_percentage: number;
  require_discount_approval: boolean;
  seller_inactivity_warning_days: number;
  seller_inactivity_block_days: number;
  offline_mode_enabled: boolean;
  mobile_settings_version: number;
  require_current_exchange_rate: boolean;
  default_currency_id: number | null;
}

export const DEFAULT_MOBILE_SETTINGS: MobileSettings = {
  expiration_days_alert_inventory_client: 15,
  min_stock_quantity_alert_inventory_client: 0,
  inventory_alerts_enabled: true,
  validate_order_stock: false,
  document_type: 'COTIZACIONES',
  show_inventory_in_mobile: true,
  quotation_validity_days: 30,
  max_order_items: 30,
  max_discount_percentage: 100,
  require_discount_approval: false,
  seller_inactivity_warning_days: 90,
  seller_inactivity_block_days: 105,
  offline_mode_enabled: true,
  mobile_settings_version: 1,
  require_current_exchange_rate: true,
  default_currency_id: null
};

@Injectable({
  providedIn: 'root'
})
export class MobileSettingsService {
  private readonly storageKey = 'mobile_settings_difritz';
  private readonly settingsSubject = new BehaviorSubject<MobileSettings>({...DEFAULT_MOBILE_SETTINGS});
  private readonly localLoaded: Promise<MobileSettings>;

  readonly settings$: Observable<MobileSettings> = this.settingsSubject.asObservable();

  constructor(private storage: StorageService) {
    this.localLoaded = this.loadLocal();
  }

  get current(): MobileSettings {
    return this.settingsSubject.value;
  }

  whenReady(): Promise<MobileSettings> {
    return this.localLoaded;
  }

  async loadLocal(): Promise<MobileSettings> {
    const stored = await this.storage.getObject(this.storageKey);
    if (stored) {
      const normalized = this.normalize(stored, DEFAULT_MOBILE_SETTINGS);
      if (normalized.mobile_settings_version >= this.current.mobile_settings_version) {
        this.settingsSubject.next(normalized);
      }
    }
    return this.current;
  }

  async applyRemote(remote: Partial<MobileSettings> | null | undefined): Promise<MobileSettings> {
    await this.localLoaded;
    if (!remote || typeof remote !== 'object') {
      return this.current;
    }

    const local = this.current;
    const remoteVersion = this.numberValue(
      (remote as any).mobile_settings_version,
      local.mobile_settings_version,
      1
    );

    if (remoteVersion < local.mobile_settings_version) {
      return local;
    }

    const normalized = this.normalize(remote, local);
    this.settingsSubject.next(normalized);
    await this.storage.setObject(this.storageKey, normalized);
    return normalized;
  }

  private normalize(source: any, fallback: MobileSettings): MobileSettings {
    return {
      expiration_days_alert_inventory_client: this.numberValue(source.expiration_days_alert_inventory_client, fallback.expiration_days_alert_inventory_client, 0),
      min_stock_quantity_alert_inventory_client: this.numberValue(source.min_stock_quantity_alert_inventory_client, fallback.min_stock_quantity_alert_inventory_client, 0),
      inventory_alerts_enabled: this.booleanValue(source.inventory_alerts_enabled, fallback.inventory_alerts_enabled),
      validate_order_stock: this.booleanValue(source.validate_order_stock, fallback.validate_order_stock),
      document_type: this.documentTypeValue(source.document_type, fallback.document_type),
      show_inventory_in_mobile: this.booleanValue(source.show_inventory_in_mobile, fallback.show_inventory_in_mobile),
      quotation_validity_days: this.numberValue(source.quotation_validity_days, fallback.quotation_validity_days, 0),
      max_order_items: this.numberValue(source.max_order_items, fallback.max_order_items, 1),
      max_discount_percentage: this.numberValue(source.max_discount_percentage, fallback.max_discount_percentage, 0, 100),
      require_discount_approval: this.booleanValue(source.require_discount_approval, fallback.require_discount_approval),
      seller_inactivity_warning_days: this.numberValue(source.seller_inactivity_warning_days, fallback.seller_inactivity_warning_days, 0),
      seller_inactivity_block_days: this.numberValue(source.seller_inactivity_block_days, fallback.seller_inactivity_block_days, 1),
      offline_mode_enabled: this.booleanValue(source.offline_mode_enabled, fallback.offline_mode_enabled),
      mobile_settings_version: this.numberValue(source.mobile_settings_version, fallback.mobile_settings_version, 1),
      require_current_exchange_rate: this.booleanValue(source.require_current_exchange_rate, fallback.require_current_exchange_rate),
      default_currency_id: this.nullableId(source.default_currency_id, fallback.default_currency_id)
    };
  }

  private booleanValue(value: any, fallback: boolean): boolean {
    if (value === true || value === 1 || value === '1' || value === 'true') {
      return true;
    }
    if (value === false || value === 0 || value === '0' || value === 'false') {
      return false;
    }
    return fallback;
  }

  private documentTypeValue(value: any, fallback: DocumentType): DocumentType {
    return value === 'COTIZACIONES' || value === 'PEDIDOS' ? value : fallback;
  }

  private numberValue(value: any, fallback: number, min: number, max?: number): number {
    const parsed = Number(value);
    if (!isFinite(parsed)) {
      return fallback;
    }
    const bounded = Math.max(min, max == null ? parsed : Math.min(max, parsed));
    return Math.floor(bounded);
  }

  private nullableId(value: any, fallback: number | null): number | null {
    if (value === null) {
      return null;
    }
    if (value === undefined || value === '') {
      return fallback;
    }
    const parsed = Number(value);
    return isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }
}
